import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { camelCase as _camelCase } from 'lodash';
import type { ReactElement, ReactNode } from 'react';

export interface SitecoreResponse {
  spa: {
    [key: string]: unknown;
    dictionary: SitecoreDictionaryNode[];
  };
}

type SitecoreDictionaryNode = SitecoreDictionary | SitecoreDictionaryItem;

interface SitecoreDictionary {
  [key: string]: SitecoreDictionaryNode[];
}

interface SitecoreDictionaryItem {
  // Note: Name and Value arSitecoreDictionaryIteme capitalised
  Name: string;
  Value: string;
}

const isSitecoreDictionaryItem = (item: SitecoreDictionaryNode): item is SitecoreDictionaryItem =>
  'Name' in item && 'Value' in item;

export interface SitecoreTransformedResponse {
  sitecorePayload: unknown;
  contentRefData: ContentRefData;
}

export interface ContentRefData {
  coverInclusions?: CoverInclusions;
  errors?: Errors;
}

interface CoverInclusions extends DictionaryNode {
  [key: string]: {
    name: DictionaryContent;
    description: DictionaryContent;
    inclusionDescription?: DictionaryContent;
    included: DictionaryContent[];
    notIncluded: DictionaryContent[];
  };
}

interface Errors extends DictionaryNode {
  [key: string]: {
    message: string;
    question: string;
    errorType: string;
  };
}

interface DictionaryNode {
  [key: string]: DictionaryContent | DictionaryContent[] | DictionaryNode;
}

type DictionaryContent = string | ReactElement | ReactElement[];

export const parseJSX = (content: DictionaryContent): ReactNode =>
  typeof content === 'string' ? parse(DOMPurify.sanitize(content)) : content;

export const formatContentKey = (s: string) => _camelCase(s).trim(); // We use camelCase but Sitecore uses lower-kebab-case

export const getNodeInfo = (key: string) => ({
  formattedKey: formatContentKey(key.toLowerCase().replace('list___', '')),
  asList: key.toLowerCase().startsWith('list___'),
});

export const transformSitecoreDictionary = (
  scDictionaryNodes: SitecoreDictionaryNode[],
  path = ''
): DictionaryNode =>
  scDictionaryNodes.reduce((output, scDictNode) => {
    // Transform SitecoreDictionaryItem
    if (isSitecoreDictionaryItem(scDictNode)) {
      if (scDictNode.Name) {
        const { formattedKey } = getNodeInfo(scDictNode.Name);
        if (!formattedKey) return output; // Skip when no valid key
        if (output[formattedKey]) {
          console.warn(`Duplicate Name at '${path}.${scDictNode.Name}'`);
          return output; // Skip, don't override previous value
        }
        output[formattedKey] = scDictNode.Value;
      } else {
        console.warn(`Missing Name at '${path}.${scDictNode.Name}'`);
      }
      return output;
    }

    // Transform SitecoreDictionary
    Object.keys(scDictNode).forEach((nodeKey) => {
      const { formattedKey, asList } = getNodeInfo(nodeKey);
      if (!formattedKey) return; // Skip, when no valid key
      if (output[formattedKey]) {
        console.warn(`Duplicate Name at '${path}.${nodeKey}'`);
        return; // Skip, don't override previous value
      }
      if (asList) {
        output[formattedKey] = transformSitecoreDictionaryNodeToList(
          scDictNode[nodeKey],
          `${path}.${nodeKey}`
        );
      } else {
        output[formattedKey] = transformSitecoreDictionary(
          scDictNode[nodeKey],
          `${path}.${nodeKey}`
        );
      }
    });

    return output;
  }, {} as DictionaryNode);

const transformSitecoreDictionaryNodeToList = (
  scDictionaryNodes: SitecoreDictionaryNode[],
  path = ''
): DictionaryContent[] =>
  scDictionaryNodes.reduce((output, scDictNode) => {
    // Only parse SitecoreDictionaryItems, ignoring other types of children
    if (isSitecoreDictionaryItem(scDictNode)) {
      const content = scDictNode.Value;
      if (!content) console.warn(`Value is empty at '${path}.${scDictNode.Name}'`);
      output.push(content);
    }
    return output;
  }, [] as DictionaryContent[]);
